/**
 * useElectron Hook
 * Provides access to Electron APIs in React components
 * Falls back gracefully when running in web browser
 */

import { useEffect, useState, useCallback } from 'react';

// Types from the Electron API
interface ElectronAPI {
  platform: {
    isElectron: true;
    isOnline: () => Promise<boolean>;
    onConnectivityChange: (callback: (online: boolean) => void) => () => void;
    getAppVersion: () => Promise<string>;
    getDeviceId: () => Promise<string>;
  };
  database: {
    createOrder: (order: unknown) => Promise<unknown>;
    getOrders: (branchId: string, filters?: unknown) => Promise<unknown[]>;
    getOrder: (orderId: string) => Promise<unknown>;
    updateOrderStatus: (orderId: string, status: string) => Promise<void>;
    getCategories: (branchId: string) => Promise<unknown[]>;
    getMenuItems: (categoryId: string) => Promise<unknown[]>;
    getAllMenuItems: (branchId: string) => Promise<unknown[]>;
    getMenuItemVariants: (menuItemId: string) => Promise<unknown[]>;
    createReservation: (reservation: unknown) => Promise<unknown>;
    getReservations: (branchId: string, date?: string) => Promise<unknown[]>;
    updateReservationStatus: (id: string, status: string) => Promise<void>;
    adjustInventory: (adjustment: unknown) => Promise<void>;
    getInventoryLevels: (branchId: string) => Promise<unknown[]>;
    getRawMaterials: (branchId: string) => Promise<unknown[]>;
    getOrCreateTableSession: (branchId: string, tableNumber: string) => Promise<unknown>;
    completeTableSession: (sessionId: string) => Promise<void>;
    getActiveTableSessions: (branchId: string) => Promise<unknown[]>;
    getBranches: (restaurantId: string) => Promise<unknown[]>;
    getBranch: (branchId: string) => Promise<unknown>;
    getDeliveryZones: (branchId: string) => Promise<unknown[]>;
  };
  sync: {
    getStatus: () => Promise<SyncStatus>;
    forceSync: () => Promise<SyncResult>;
    refreshMenu: () => Promise<void>;
    getPendingCount: () => Promise<number>;
    getPendingOperations: () => Promise<unknown[]>;
    checkConnectivity: () => Promise<boolean>;
    prefetchMenu: (branchId: string) => Promise<PrefetchResult>;
    pullOrders: (branchId: string) => Promise<{ success: boolean; updatedCount: number; error?: string }>;
    onSyncProgress: (callback: (progress: unknown) => void) => () => void;
  };
  auth: {
    loginStaff: (username: string, password: string) => Promise<unknown>;
    getCachedSession: () => Promise<unknown>;
    logout: () => Promise<void>;
    validateSession: () => Promise<boolean>;
  };
  updater: {
    checkForUpdates: () => Promise<UpdateInfo | null>;
    downloadUpdate: () => Promise<void>;
    installUpdate: () => void;
    onUpdateProgress: (callback: (progress: unknown) => void) => () => void;
    onUpdateAvailable: (callback: (info: UpdateInfo) => void) => () => void;
    onUpdateDownloaded: (callback: (info: UpdateInfo) => void) => () => void;
  };
}

interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncAt: string | null;
  pendingCount: number;
  error: string | null;
  conflicts?: Array<{
    id: string;
    entityType: string;
    entityId: string;
    field: string;
    localValue: unknown;
    serverValue: unknown;
    localUpdatedAt: string;
    serverUpdatedAt: string;
    description?: string;
  }>;
}

interface SyncResult {
  success: boolean;
  syncedCount: number;
  failedCount: number;
  errors: string[];
}

interface PrefetchResult {
  success: boolean;
  categoriesCount: number;
  itemsCount: number;
  variantsCount: number;
  error?: string;
}

interface UpdateInfo {
  version: string;
  releaseDate?: string;
  releaseNotes?: string;
}

// Extend Window interface
declare global {
  interface Window {
    electronAPI?: ElectronAPI;
    versions?: {
      node: () => string;
      chrome: () => string;
      electron: () => string;
    };
  }
}

/**
 * Check if running in Electron
 */
export function isElectron(): boolean {
  return typeof window !== 'undefined' && 
         window.electronAPI !== undefined && 
         window.electronAPI.platform?.isElectron === true;
}

/**
 * Get the Electron API (throws if not in Electron)
 */
export function getElectronAPI(): ElectronAPI {
  if (!isElectron()) {
    throw new Error('Not running in Electron');
  }
  return window.electronAPI!;
}

/**
 * Hook to access Electron functionality
 */
export function useElectron() {
  const [isElectronApp] = useState(isElectron);
  const [isOnline, setIsOnline] = useState(true);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState<UpdateInfo | null>(null);

  // Subscribe to connectivity changes
  useEffect(() => {
    if (!isElectronApp) return;

    const api = getElectronAPI();
    
    // Get initial online status
    api.platform.isOnline().then(setIsOnline);
    
    // Subscribe to changes
    const unsubscribe = api.platform.onConnectivityChange(setIsOnline);
    
    return unsubscribe;
  }, [isElectronApp]);

  // Subscribe to sync progress
  useEffect(() => {
    if (!isElectronApp) return;

    const api = getElectronAPI();
    
    // Get initial sync status
    api.sync.getStatus().then(setSyncStatus);
    
    // Subscribe to updates
    const unsubscribe = api.sync.onSyncProgress((progress) => {
      if (typeof progress === 'object' && progress !== null) {
        api.sync.getStatus().then(setSyncStatus);
      }
    });
    
    return unsubscribe;
  }, [isElectronApp]);

  // Subscribe to update notifications
  useEffect(() => {
    if (!isElectronApp) return;

    const api = getElectronAPI();
    
    const unsubscribe = api.updater.onUpdateAvailable((info) => {
      setUpdateAvailable(info);
    });
    
    return unsubscribe;
  }, [isElectronApp]);

  // Database operations
  const database = isElectronApp ? getElectronAPI().database : null;

  // Sync operations
  const forceSync = useCallback(async () => {
    if (!isElectronApp) return null;
    return getElectronAPI().sync.forceSync();
  }, [isElectronApp]);

  const refreshMenu = useCallback(async () => {
    if (!isElectronApp) return;
    return getElectronAPI().sync.refreshMenu();
  }, [isElectronApp]);

  const prefetchMenu = useCallback(async (branchId: string) => {
    if (!isElectronApp) return null;
    return getElectronAPI().sync.prefetchMenu(branchId);
  }, [isElectronApp]);

  const pullOrders = useCallback(async (branchId: string) => {
    if (!isElectronApp) return null;
    return getElectronAPI().sync.pullOrders(branchId);
  }, [isElectronApp]);

  // Auth operations
  const loginStaff = useCallback(async (username: string, password: string) => {
    if (!isElectronApp) return null;
    return getElectronAPI().auth.loginStaff(username, password);
  }, [isElectronApp]);

  const logout = useCallback(async () => {
    if (!isElectronApp) return;
    return getElectronAPI().auth.logout();
  }, [isElectronApp]);

  const getCachedSession = useCallback(async () => {
    if (!isElectronApp) return null;
    return getElectronAPI().auth.getCachedSession();
  }, [isElectronApp]);

  // Updater operations
  const checkForUpdates = useCallback(async () => {
    if (!isElectronApp) return null;
    return getElectronAPI().updater.checkForUpdates();
  }, [isElectronApp]);

  const downloadUpdate = useCallback(async () => {
    if (!isElectronApp) return;
    return getElectronAPI().updater.downloadUpdate();
  }, [isElectronApp]);

  const installUpdate = useCallback(() => {
    if (!isElectronApp) return;
    getElectronAPI().updater.installUpdate();
  }, [isElectronApp]);

  return {
    // State
    isElectron: isElectronApp,
    isOnline,
    syncStatus,
    updateAvailable,

    // Database (only available in Electron)
    database,

    // Sync
    forceSync,
    refreshMenu,
    prefetchMenu,
    pullOrders,

    // Auth
    loginStaff,
    logout,
    getCachedSession,

    // Updater
    checkForUpdates,
    downloadUpdate,
    installUpdate,
  };
}

/**
 * Hook for offline-first data fetching
 * Uses local SQLite when in Electron, falls back to provided fetch function
 */
export function useOfflineFirst<T>(
  key: string,
  fetchFn: () => Promise<T>,
  electronFetchFn?: () => Promise<T>
): {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const { isElectron: isElectronApp } = useElectron();
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (isElectronApp && electronFetchFn) {
        // Use local database in Electron
        const result = await electronFetchFn();
        setData(result);
      } else {
        // Use remote fetch
        const result = await fetchFn();
        setData(result);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [isElectronApp, fetchFn, electronFetchFn]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

export default useElectron;
